// src/api/feedApi.ts
import api from '../api/axiosConfig';
import type {FeedItem, FeedDetailItem} from '../types/FeedItem';
import type {NetFlowPoint} from '../store/slice/feedSlice';

/**
 * 백엔드 응답이 배열 그대로 오거나 { data: [...] } / { items: [...] } 형태로 와도
 * 목록 payload를 보존한다. mapping_audit는 여기서 재계산하지 않는다.
 */
function unwrapFeedListPayload(payload: any): FeedItem[] {
  if (Array.isArray(payload)) return payload as FeedItem[];
  if (Array.isArray(payload?.data)) return payload.data as FeedItem[];
  if (Array.isArray(payload?.items)) return payload.items as FeedItem[];
  if (Array.isArray(payload?.feeds)) return payload.feeds as FeedItem[];
  return [];
}

function unwrapFeedDetailPayload(payload: any): FeedDetailItem {
  const data = payload?.data ?? payload?.item ?? payload?.feed ?? payload;
  return data as FeedDetailItem;
}

/** 피드 목록 조회 */
export const fetchFeeds = async (
    sector: string,
    sort: 'latest' | 'impact',
): Promise<FeedItem[]> => {
  const res = await api.get('/feed', {params: {sector, sort}});
  return unwrapFeedListPayload(res.data);
};

/**
 * 피드 상세 조회
 *
 * 현재 feedSaga.ts의 fetchFeedDetailSaga가 이 함수를 호출한다.
 * 백엔드 상세 응답에도 mapping_audit가 포함되어야 한다.
 *
 * 예상 엔드포인트:
 *   GET /feed/:feedId?sector=energy
 */
export async function fetchFeedDetail(params: {
  feedId: string;
  sector?: string;
}): Promise<FeedDetailItem> {
  const {feedId, sector} = params;
  const res = await api.get(`/feed/${encodeURIComponent(feedId)}`, {
    params: sector ? {sector} : undefined,
  });
  return unwrapFeedDetailPayload(res.data);
}

/** 최근 본 뉴스 (옵션) */
export async function getRecentViewedNews(): Promise<FeedItem[]> {
  const res = await api.get('/news/recent');
  return unwrapFeedListPayload(res.data);
}

/* =========================
 * 상세 팝업 관련 API
 * ========================= */

/** 저장(북마크) — sector 동봉 */
export interface SaveEventPayload {
  eventId: string;
  sector?: string;
}

/**
 * 백엔드가 기존 `/feed/:id/save` 엔드포인트를 사용한다면:
 *  - body 없이 sector는 쿼리스트링으로 전달(하위 호환)
 * 백엔드가 JSON POST를 원하면, 아래 라인을 바꿔서 body로 sector를 보내면 된다.
 */
export async function saveEvent(payload: SaveEventPayload): Promise<void> {
  const {eventId, sector} = payload;
  await api.post(`/feed/${encodeURIComponent(eventId)}/save`, null, {
    params: sector ? {sector} : undefined,
  });
}

/** 저장 해제 */
export async function removeSavedEvent(eventId: string): Promise<void> {
  await api.delete(`/feed/${encodeURIComponent(eventId)}/save`);
}

/** 알림 생성(이벤트 기반) */
export interface CreateAlertFromEventPayload {
  eventId: string;
  channels?: { webpush: boolean; email: boolean };
  filters?: any | null;
}

export async function createAlertFromEvent(payload: CreateAlertFromEventPayload): Promise<void> {
  await api.post('/alerts/from-event', payload);
}

/** 백테스트 실행 */
export interface RunBacktestPayload {
  eventId?: string | null;
  strategyKey: string;
  params?: Record<string, any>;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export async function runBacktest(payload: RunBacktestPayload): Promise<any> {
  const res = await api.post('/backtest/run', payload);
  return res.data;
}

/** 워치리스트 추가/제거 */
export interface AddWatchlistPayload {
  tickers: { code: string; name: string; market?: string }[];
}

export async function addWatchlist(payload: AddWatchlistPayload): Promise<void> {
  await api.post('/watchlist/add', payload);
}

export interface RemoveWatchlistPayload {
  tickers: string[];
}

export async function removeWatchlist(payload: RemoveWatchlistPayload): Promise<void> {
  // DELETE body 제약 → POST로 처리
  await api.post('/watchlist/remove', payload);
}

/* =========================
 * OHLCV + 순매수 시계열
 * ========================= */
/**
 * GET /feed/stocks/:ticker/daily?from=YYYY-MM-DD&to=YYYY-MM-DD&view=net
 * 백엔드에서 view=net일 때,
 *   [{ date, open, high, low, close, volume, indiv_net, foreign_net, inst_net }, ...] 형태로 반환
 */
export async function fetchDailyNetSeries(params: {
  ticker: string;
  from: string;
  to: string;
}): Promise<NetFlowPoint[]> {
  const {ticker, from, to} = params;
  const res = await api.get<NetFlowPoint[]>(
      `/feed/stocks/${encodeURIComponent(ticker)}/daily`,
      {params: {from, to, view: 'net'}},
  );
  return res.data ?? [];
}
